import { useSyncExternalStore } from 'react'

import { ApiError } from '@/lib/api/errors'
import type { ClientConfig } from '@/types/domain'

/**
 * What the app knows about the e-mail-verification gate.
 *
 * It now knows it from two sources, and they are not equal:
 *
 * 1. **`GET /v1/config`** (`verification`) — whether the gate is switched on
 *    in this deployment at all, and, for an unconfirmed address, the instant
 *    the gateway itself resolved for the pause (`uploadsPausedAt`, already
 *    `created_at + graceHours`). This is what lets the banner stay silent
 *    where the gate is off, announce the deadline *before* anything is
 *    refused, and show the pause without waiting for a refusal.
 * 2. **An observed `403 email_unverified`** — the store below. It is kept,
 *    and it wins where the two disagree: a refusal is something that
 *    actually happened to this person, and a config that says otherwise is
 *    stale, cached or mis-deployed. It is also the only source for the
 *    upload-link gate, which has no grace window and appears in no config.
 *
 * The store is module-level rather than react-query state on purpose: the
 * refusal arrives inside whichever mutation a screen was running, and every
 * surface of the shell has to react to it. A screen catching an error from an
 * upload, an analyse or an upload-link call passes it to
 * `noteEmailUnverified` and is done; the banner is the shell's problem.
 */
export interface VerificationPause {
  /** The address the gateway named in the refusal. */
  email?: string
  /** RFC3339, when the pause began. Absent on the upload-link refusal. */
  pausedSince?: string
  graceHours?: number
  /**
   * `uploads` — new uploads and analyses are paused since `pausedSince`.
   * `uploadLink` — creating a phone upload link needs a confirmed address
   * from day one, so this refusal carries no deadline and must not be
   * rendered as a countdown.
   */
  scope: 'uploads' | 'uploadLink'
}

let current: VerificationPause | null = null
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Records a caught error if it is the verification refusal, and answers what
 * it was — so a caller can branch ("nichts hochgeladen") without parsing the
 * body a second time. Any other error leaves the store alone and returns
 * null, which makes this safe to call in every upload catch block.
 */
export function noteEmailUnverified(error: unknown): VerificationPause | null {
  const api = ApiError.from(error)
  if (api.status !== 403 || api.code !== 'email_unverified') return null

  const details = api.details ?? {}
  const pausedSince = typeof details.pausedSince === 'string' ? details.pausedSince : undefined
  const pause: VerificationPause = {
    email: typeof details.email === 'string' ? details.email : undefined,
    pausedSince,
    graceHours: typeof details.graceHours === 'number' ? details.graceHours : undefined,
    // No deadline in the body means the upload-link refusal, which has none.
    scope: pausedSince ? 'uploads' : 'uploadLink',
  }
  current = pause
  emit()
  return pause
}

/** Forgets the pause — after the address is confirmed, or on sign-out. */
export function clearVerificationPause() {
  if (current === null) return
  current = null
  emit()
}

export function useVerificationPause(): VerificationPause | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  )
}

/** Test seam: the store is module state and outlives a test's render. */
export function __setVerificationPause(pause: VerificationPause | null) {
  current = pause
  emit()
}

export type VerificationNotice =
  /** Unconfirmed, nothing refused yet. `pausesAt` only where the gate is on
   *  and the gateway named the instant; without it the reminder promises no
   *  deadline, because there is none to promise. */
  | { kind: 'reminder'; email: string; pausesAt?: string }
  | { kind: 'paused'; email: string; pausedSince?: string }
  | { kind: 'linkBlocked'; email: string }

export interface VerificationInput {
  user: { email?: string; emailVerified?: boolean } | null
  /** `undefined` while `GET /v1/config` has not answered (or failed). */
  config?: ClientConfig
  pause: VerificationPause | null
  dismissed?: boolean
  /** Injected so the deadline's two sides can be tested without waiting. */
  now?: Date
}

/**
 * Which of the states the banner is in, as a pure decision so the wording can
 * be checked without a render.
 *
 * The order of the rules is the point:
 *
 * - A **recorded refusal** is decided first, whatever the config says. It is
 *   the one thing we know happened, and the upload-link gate exists nowhere
 *   else.
 * - `verification.enabled === false` then means *nothing at all* — not even
 *   the quiet reminder. The address may well be unconfirmed; on a deployment
 *   that never pauses anything, saying so is nagging about a consequence that
 *   does not exist.
 * - With the gate on and the address unconfirmed, `uploadsPausedAt` decides
 *   between the two honest halves: before it, a reminder that names the day;
 *   at or after it, the pause — without waiting for somebody to run into a
 *   403 first. The instant is the gateway's own; `graceHours` is never
 *   arithmetic here.
 * - While the config has not answered, the old behaviour stands: reminder for
 *   an unconfirmed address, pause only on a refusal. It promises nothing it
 *   cannot keep.
 */
export function verificationNotice({
  user,
  config,
  pause,
  dismissed = false,
  now = new Date(),
}: VerificationInput): VerificationNotice | null {
  if (!user) return null

  // `emailVerified` is reported by both; the config's is the fresher read of
  // the same column. `undefined` on both means an older gateway, and nagging
  // on a field that was never reported would be a guess.
  const verified = config?.verification.emailVerified ?? user.emailVerified
  const email = pause?.email || user.email || ''

  // A refusal outranks everything: it happened.
  if (verified !== true) {
    if (pause?.scope === 'uploads') return { kind: 'paused', email, pausedSince: pause.pausedSince }
    if (pause?.scope === 'uploadLink') return { kind: 'linkBlocked', email }
  }

  if (verified !== false) return null
  if (config && !config.verification.enabled) return null

  const pausesAt = config?.verification.uploadsPausedAt
  const deadline = pausesAt ? new Date(pausesAt) : null
  if (deadline && !Number.isNaN(deadline.getTime()) && deadline.getTime() <= now.getTime()) {
    // Past the gateway's own deadline: uploads are refused, whether or not
    // this session has tried one yet.
    return { kind: 'paused', email, pausedSince: pausesAt }
  }

  if (dismissed) return null
  return deadline && !Number.isNaN(deadline.getTime())
    ? { kind: 'reminder', email, pausesAt }
    : { kind: 'reminder', email }
}
